import React, { useEffect, useState, createElement } from 'react';
import { MenuIcon, XIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
declare global {
  interface Window {
    Cal?: ((...args: unknown[]) => void) & {
      loaded?: boolean;
      ns?: Record<string, (...args: unknown[]) => void>;
      q?: unknown[];
    };
  }
}
export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileClosing, setMobileClosing] = useState(false);
  const [selectedMobileLink, setSelectedMobileLink] = useState<string | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.innerHTML = `
      (function (C, A, L) {
        let p = function (a, ar) { a.q.push(ar); };
        let d = C.document;
        C.Cal = C.Cal || function () {
          let cal = C.Cal; let ar = arguments;
          if (!cal.loaded) { cal.ns = {}; cal.q = cal.q || []; d.head.appendChild(d.createElement("script")).src = A; cal.loaded = true; }
          if (ar[0] === L) { const api = function () { p(api, arguments); }; const namespace = ar[1]; api.q = api.q || []; if(typeof namespace === "string"){cal.ns[namespace] = cal.ns[namespace] || api;p(cal.ns[namespace], ar);p(cal, ["initNamespace", namespace]);} else p(cal, ar); return;} p(cal, ar);
        };
      })(window, "https://app.cal.com/embed/embed.js", "init");
      Cal("init", "30min", {origin:"https://app.cal.com"});
      Cal.ns["30min"]("ui", {"hideEventTypeDetails":false,"layout":"month_view"});
    `;
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);
  const navLinks = [{
    label: 'Work',
    href: '#work'
  }, {
    label: 'About',
    href: '#about'
  }, {
    label: 'Contact',
    href: '#contact'
  }];
  const handleNavClick = (href: string) => {
    if (href.startsWith('#')) {
      if (window.location.pathname !== '/') {
        navigate('/');
        window.setTimeout(() => {
          document.querySelector(href)?.scrollIntoView({
            behavior: 'smooth'
          });
        }, 100);
      } else {
        document.querySelector(href)?.scrollIntoView({
          behavior: 'smooth'
        });
      }
    } else {
      navigate(href);
    }
  };
  const closeMobileMenu = () => {
    if (mobileClosing) return;
    setMobileClosing(true);
    window.setTimeout(() => {
      setMobileOpen(false);
      setMobileClosing(false);
      setSelectedMobileLink(null);
    }, 220);
  };
  const handleMobileNavClick = (href: string, label: string) => {
    if (mobileClosing) return;
    setSelectedMobileLink(label);
    setMobileClosing(true);
    window.setTimeout(() => {
      setMobileOpen(false);
      setMobileClosing(false);
      setSelectedMobileLink(null);
      handleNavClick(href);
    }, 220);
  };
  const openMobileMenu = () => {
    setSelectedMobileLink(null);
    setMobileClosing(false);
    setMobileOpen(true);
  };
  const handleMobileBooking = () => {
    if (mobileClosing) return;
    setSelectedMobileLink('Book a call');
    setMobileClosing(true);
    window.setTimeout(() => {
      setMobileOpen(false);
      setMobileClosing(false);
      setSelectedMobileLink(null);
    }, 220);
  };
  return <>
      {/* ── Desktop ── */}
      <div className={`hidden md:block fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'backdrop-blur-md shadow-sm' : 'bg-transparent'}`} style={{
      background: scrolled ? 'rgba(247, 245, 238, 0.88)' : 'transparent'
    }} data-id="element-115" data-mp="3svbph7x6h00x">
        <nav className="max-w-[1200px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between" data-id="element-116" data-mp="337z6yjo74us3">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center" style={{
          textDecoration: 'none'
        }} data-id="element-117" data-mp="2y7tmpok9aak1">
            <span className="font-aspekta text-lg tracking-tight whitespace-nowrap" style={{
            color: 'var(--fg)'
          }} data-id="element-118" data-mp="2p7oh9jymhm8i">
              Stella Pangilinan
            </span>
          </Link>

          {/* Right-aligned navigation & CTA */}
          <div className="hidden md:flex items-center justify-end gap-6" data-id="element-119" data-mp="2ffsgg155ji0l">
            {navLinks.map(link => <button key={link.label} onClick={() => handleNavClick(link.href)} className="font-aspekta text-sm font-normal bg-transparent border-none cursor-pointer transition-colors" style={{
            color: 'var(--fg)'
          }} onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--fg)'} data-id="element-120" data-mp="25jxqekylmkns">
                {link.label}
              </button>)}
            <button data-cal-link="go.launch/30min" data-cal-namespace="30min" data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true"}' className="font-aspekta h-10 px-6 text-sm font-normal rounded-full cursor-pointer transition-colors" style={{
            background: 'var(--accent)',
            color: '#FFFFFF',
            border: 'none'
          }} onMouseEnter={e => e.currentTarget.style.background = '#D95D3E'} onMouseLeave={e => e.currentTarget.style.background = 'var(--accent)'} data-id="element-121" data-mp="3tirh0d397vg0">
              Book a call
            </button>
          </div>
        </nav>
      </div>

      {/* ── Mobile ── */}
      {!mobileOpen && <header className="fixed top-0 left-0 right-0 z-50 md:hidden" style={{
      padding: '12px 16px'
    }} data-id="element-122" data-mp="3hh9pjftmj8qh">
          <div className="flex items-center justify-between" style={{
        background: scrolled ? 'rgba(247, 245, 238, 0.9)' : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(12px)' : 'none',
        border: `1px solid ${scrolled ? 'var(--border)' : 'transparent'}`,
        borderRadius: 9999,
        padding: '10px 18px',
        boxShadow: scrolled ? '0 2px 16px rgba(0,0,0,0.05)' : 'none',
        transition: 'background 250ms ease-out, border-color 250ms ease-out, box-shadow 250ms ease-out'
      }} data-id="element-123" data-mp="2orjbf6rzs6ud">
            <Link to="/" style={{
          textDecoration: 'none'
        }} aria-label="Stella Pangilinan home" data-id="element-124" data-mp="1vxecpinxqzrk">
              <span className="font-aspekta text-base tracking-tight whitespace-nowrap" style={{
            color: 'var(--fg)'
          }} data-id="element-125" data-mp="1mvnainsltb2g">
                Stella Pangilinan
              </span>
            </Link>
            <button type="button" onClick={openMobileMenu} style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--fg)',
          padding: 4
        }} aria-label="Open menu" aria-expanded="false" data-id="element-126" data-mp="2elrqma9bzbxv">
              <MenuIcon size={22} aria-hidden="true" data-id="element-127" data-mp="2yb25xn55zuvg" />
            </button>
          </div>
        </header>}

      {mobileOpen && <div className={`mobile-menu-overlay fixed inset-0 z-[60] flex min-h-screen flex-col md:hidden ${mobileClosing ? 'is-closing' : ''}`} style={{
      background: 'var(--bg)'
    }} role="dialog" aria-modal="true" aria-label="Main navigation" data-id="element-128" data-mp="y9vkw1vzrkps">
          <header className="flex items-center justify-between px-6 py-6" data-id="element-129" data-mp="jlhly21p4gei">
            <Link to="/" onClick={closeMobileMenu} style={{
          textDecoration: 'none'
        }} aria-label="Stella Pangilinan home" data-id="element-130" data-mp="1h63ufhw1ehqw">
              <span className="font-aspekta text-xl tracking-tight whitespace-nowrap" style={{
            color: 'var(--fg)'
          }} data-id="element-131" data-mp="1787mt02b64xj">
                Stella Pangilinan
              </span>
            </Link>
            <button type="button" onClick={closeMobileMenu} className="flex h-11 w-11 items-center justify-center rounded-full" style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--fg)'
        }} aria-label="Close menu" data-id="element-132" data-mp="3rxdenhyi8c5c">
              <XIcon size={28} aria-hidden="true" data-id="element-133" data-mp="27f2w9rku2q4p" />
            </button>
          </header>

          <nav className="flex flex-1 flex-col justify-center px-8 pb-16" aria-label="Mobile navigation" data-id="element-134" data-mp="2cefkafnfy38v">
            <div className="flex flex-col items-start" data-id="element-135" data-mp="2h93caohnlfs7">
              {navLinks.map((link, index) => {
            const isSelected = selectedMobileLink === link.label;
            return <button key={link.label} type="button" onClick={() => handleMobileNavClick(link.href, link.label)} className="mobile-menu-item w-full border-0 bg-transparent py-3 text-left text-[clamp(2.5rem,12vw,4.5rem)] font-semibold leading-[1.08] tracking-[-0.06em]" style={{
              animationDelay: mobileClosing ? `${index * 25}ms` : `${100 + index * 45}ms`,
              color: isSelected ? 'var(--accent)' : 'var(--fg)',
              cursor: mobileClosing ? 'default' : 'pointer',
              transition: 'color 120ms ease-out'
            }} disabled={mobileClosing} data-id="element-136" data-mp="2r797yocv9pom" data-mpi={index}>
                    {link.label}
                  </button>;
          })}
            </div>

            <button type="button" data-cal-link="go.launch/30min" data-cal-namespace="30min" data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true"}' onClick={handleMobileBooking} className="font-aspekta mobile-menu-item mt-12 w-full rounded-full px-6 py-4 text-base font-normal" style={{
          animationDelay: mobileClosing ? '110ms' : '310ms',
          background: selectedMobileLink === 'Book a call' ? 'var(--fg)' : 'var(--accent)',
          color: '#FFFFFF',
          border: 'none',
          cursor: mobileClosing ? 'default' : 'pointer',
          transition: 'background-color 120ms ease-out, color 120ms ease-out'
        }} disabled={mobileClosing} data-id="element-137" data-mp="l17nbq12vxvv">
              Book a call
            </button>
          </nav>
        </div>}
    </>;
}